<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

import { Text } from '~ds/components/ui/typography';
import { useIpxBackgroundStyle } from '~kui/composables/useIpxBackgroundStyle';
import { useI18n } from 'vue-i18n';

interface Props {
  imagePath?: string | null;
  title: string;
  subTitle?: string;
  variant?: 'default' | 'full';
  wide?: boolean;
  square?: boolean;
  /** When set, the card renders as a crawlable `<a>` (NuxtLink) to this destination. */
  to?: string | RouteLocationRaw;
  /** Refuses activation on the inner control itself, so the card also leaves the tab order. */
  disabled?: boolean;
  /** Overrides the default "View {title}" link name, for cards whose action is not "view". */
  ariaLabel?: string;
  /** Clamps the plate's title to two lines, so a row of cards keeps one height. */
  clampTitle?: boolean;
  /** `row` grows the card to the size a scrollable hub row draws its cards at. */
  size?: 'default' | 'row';
}

const props = withDefaults(defineProps<Props>(), {
  subTitle: '',
  imagePath: null,
  variant: 'default',
  wide: false,
  square: false,
  to: undefined,
  disabled: false,
  ariaLabel: undefined,
  clampTitle: false,
  size: 'default',
});

const emit = defineEmits<{
  click: [];
}>();

// Wide/full cards render larger, so request more pixels; routed through ipx (resized webp).
const backgroundWidth = computed(() => (props.wide || props.variant === 'full' ? 800 : 400));
const cardBackgroundStyle = useIpxBackgroundStyle(() => props.imagePath, { width: backgroundWidth });

// The clickable is `overflow-hidden` around a blurred plate, which the global Safari fix masks to
// its own box, so a focus outline on it is clipped away. The ring is drawn on this wrapper instead.
const rootClasses = computed(() =>
  twMerge(cva('relative rounded-2xl has-focus-visible:outline-3 has-focus-visible:outline-offset-3 has-focus-visible:outline-ring', {
    variants: {
      variant: {
        default: 'w-fit',
        full: 'w-full',
      },
      wide: {
        true: 'col-span-2',
        false: '',
      },
    },
  })({ variant: props.variant, wide: props.wide })),
);

const widthVariantClasses = computed(() =>
  twMerge(cva('relative bg-cover bg-no-repeat flex flex-col cursor-pointer rounded-2xl bg-center p-2 justify-between overflow-hidden', {
    variants: {
      variant: {
        default: 'min-w-50 max-w-50',
        full: 'w-full',
      },
      square: {
        true: 'aspect-square',
        false: 'h-45',
      },
      // `wide` is declared so the compound variant below can match; the
      // wide=false branch intentionally adds no classes.
      wide: {
        true: '',
        false: '',
      },
      // Declared last so its sizing wins the twMerge over the variant/square defaults.
      size: {
        default: '',
        row: 'h-55 w-70 min-w-70 max-w-70',
      },
    },
    compoundVariants: [
      // Wide + square cards span two columns, so override the 1:1 ratio.
      { square: true, wide: true, class: 'aspect-[2/1]' },
    ],
  })({ variant: props.variant, square: props.square, wide: props.wide, size: props.size })),
);

const { t } = useI18n();
</script>

<template>
  <div :class="rootClasses">
    <KUICoreClickable
      :to="to"
      :disabled="disabled"
      :style="cardBackgroundStyle"
      :class="widthVariantClasses"
      :aria-label="ariaLabel ?? t('unified.common.assetCard.view', { title })"
      class="bg-white border-2 border-white focus-visible:outline-none"
      @click="emit('click')"
    >
      <span class="block">
        <slot name="header" />
      </span>

      <KUICoreBlurredEdge
        as="span"
        blur-size="2xs"
        rounding="3xl"
        class="w-fit flex flex-col gap-3 mt-auto text-start p-2"
        data-testid="asset-card-plate"
      >
        <Text
          as="span"
          weight="medium"
          :class="clampTitle ? 'line-clamp-2' : 'block'"
          data-testid="asset-card-title"
        >
          {{ title }}
        </Text>
        <slot name="extra" />

        <span v-if="subTitle" class="flex justify-between">
          <Text
            as="span"
            size="sm"
            class="block text-muted-foreground"
          >
            {{ subTitle }}
          </Text>
        </span>
      </KUICoreBlurredEdge>
    </KUICoreClickable>
  </div>
</template>
